import React from 'react';
import { Body5, Container, SVG_LOADING_ICON_TYPES, SvgLoadingIcon } from '@ui';
import CheckCircle from '@assets/icons/svgs/CheckCircle';
import styled from 'styled-components';
import { ResultContext } from '@/contexts/ResultContext';
import { CitationsContext } from '@/contexts/CitationsContext';
import { extractIntegerAfterHash, findBestFileNameMatch } from '@utils/usefulFuncs';
import { useColorTheme } from '@hooks';
import { extractIdFromUrl } from '@hooks/features/useOrderedCitations';
import { isUndefinedOrNull } from '@utils/usefulFuncs';
import { CitationIcon } from '@assets/icons/svgs/CitationIcon';
import { QueryControllerContext } from '@/contexts/QueryControllerContext';
import { getContextItem } from '@utils/result';
import { Modal } from '@ui';
import { CloseButton } from '@ui/buttons/CloseButton';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';

const CheckWrapper = styled.div`
  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

const CitationWrapper = styled.span`
  position: relative;
  display: inline-block;
  cursor: pointer;
  padding-right: ${({ IdValue }) =>
    IdValue?.length === 1 ? '5px' : IdValue?.length === 2 ? '8px' : IdValue?.length === 3 ? '15px' : '0px'};

  svg {
    height: 14px;
    width: 14px;

    path {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }

  &:hover {
    * {
      opacity: 0.6;
    }
  }
`;
const CitationIdWrapper = styled.div`
  position: absolute;
  top: -6px;
  right: ${({ Value }) => (Value.length === 2 ? '-1px' : Value.length === 3 ? '0px' : '-1px')};
  font-size: 8px;
  font-weight: bold;
  color: ${({ theme }) => theme.themeColors.text};
`;

function altFindHeader(string) {
  const split = string.split('-');
  const possibleHeaderNumber = parseFloat(split[split.length - 1]);
  if (!isNaN(possibleHeaderNumber)) {
    return possibleHeaderNumber;
  } else {
    return null;
  }
}

function matchFilename(string, sourceDocs) {
  return sourceDocs?.find((doc) => string.includes(doc.filename)) || null;
}

function parseChunkData(string, sourceDocs) {
  let matchedFile = matchFilename(string, sourceDocs);
  let matchedHeader = extractIntegerAfterHash(string);

  if (!sourceDocs)
    return {
      doc: null,
      chunkId: null,
    };

  if (!matchedFile) matchedFile = findBestFileNameMatch(string, sourceDocs);
  if (!matchedHeader) matchedHeader = altFindHeader(string);

  if (isNaN(matchedHeader)) {
    return {
      doc: null,
      chunkId: null,
    };
  }

  if (sourceDocs.length === 1 && !isNaN(matchedHeader)) {
    return {
      doc: sourceDocs[0],
      chunkId: matchedHeader,
    };
  }

  return {
    doc: matchedFile,
    chunkId: matchedHeader,
  };
}

export function AddingCitations() {
  const colorTheme = useColorTheme();

  return (
    <Container row verticallyCenter bottom={8}>
      <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
      <Container left={8} bottom={8}>
        <Body5>Adding Citations...</Body5>
      </Container>
    </Container>
  );
}

const CitationId = ({ value }) => {
  return <CitationIdWrapper Value={value}>{value}</CitationIdWrapper>;
};

function useGetSourceDocsInContext() {
  const { prevQueryFormData } = React.useContext(QueryControllerContext);
  return prevQueryFormData?.selectedDocs || [];
}

const debug = false;

export const Citation = ({ url, data }) => {
  const { onCitationClick } = React.useContext(CitationsContext);
  const sourceDocs = useGetSourceDocsInContext();

  const citationId = extractIdFromUrl(url);
  const { doc, chunkId } = parseChunkData(data, sourceDocs);

  const hc = (e) => {
    e.preventDefault();
    onCitationClick(chunkId, doc, citationId);
  };

  if (isUndefinedOrNull(doc)) return null;
  return (
    <CitationWrapper IdValue={citationId} onClick={hc}>
      {debug ? <Body5>{data}</Body5> : null}
      <CitationIcon />
      {citationId && <CitationId value={citationId} />}
    </CitationWrapper>
  );
};

const CitationsModal = ({ onDismiss, item }) => {
  const { fullContext: context } = React.useContext(ResultContext);
  const [modalProps] = React.useState({ maximizeWidth: true, forceHeight: false });

  if (!context) return null;

  const { contextData, allContextData } = getContextItem(item, context);
  const { doc } = item;
  const { document_url } = doc;
  const text = contextData?.text;

  const handleViewInOriginal = () => {
    const urlEncodedTextBlock = encodeURIComponent(text);
    const searchParam = `#:~:text=${urlEncodedTextBlock}`;
    window.open(document_url + searchParam, '_blank');
  };

  return (
    <Modal onDismiss={onDismiss} modalProps={modalProps}>
      <Container horizontal={8}>
        <Container row verticallyCenter spaceBetween>
          <Container>
            <Body5 bold>Citation</Body5>
          </Container>
          <CloseButton onClick={onDismiss} />
        </Container>
        <Container top={8}>
          {item?.doc ? <Body5>{item.doc.filename}</Body5> : null}
          <Container top={8}>
            {text ? <Body5>{text}</Body5> : allContextData?.map((t, i) => <Body5 key={i}>{t}</Body5>)}
          </Container>
        </Container>
        {document_url && (
          <Container top={16}>
            <SkeletonButton onClick={handleViewInOriginal}>View Original Document</SkeletonButton>
          </Container>
        )}
      </Container>
    </Modal>
  );
};

export const Citations = () => {
  const { citationsResultId, citationsStreamEnded, context } = React.useContext(ResultContext);
  const { highlightContextItem, onCitationDismiss } = React.useContext(CitationsContext);

  return (
    <Container>
      {citationsResultId && citationsStreamEnded && (
        <Container verticallyCenter top={0} bottom={16} height={48} left={16}>
          <CheckWrapper>
            <CheckCircle />
          </CheckWrapper>
          <Container left={8} bottom={3}>
            <Body5 bold>Citations Inserted</Body5>
          </Container>
        </Container>
      )}
      {highlightContextItem !== null && (
        <CitationsModal item={highlightContextItem} context={context} onDismiss={onCitationDismiss} />
      )}
    </Container>
  );
};
